import { createContext, useContext, useState, type ReactNode } from "react";
import type { Card } from "@solitaire/game-engine";
import type { CardSetManifest } from "@solitaire/ui";
import { getCardFaceUrl, getCardBackUrl } from "@solitaire/ui";
import { GAME_CONFIG, type TableBackgroundKey } from "../config.js";

export type AnimationSpeed = "slow" | "normal" | "fast";
export const ANIMATION_SPEED_DURATION: Record<AnimationSpeed, number> = {
  slow: 0.7,
  normal: 0.4,
  fast: 0.2,
};

// ============================================================
// Bekannte Karten-Sets (später per Fetch aus /cards/sets/*/manifest.json)
// ============================================================
const MANIFESTS: CardSetManifest[] = [
  {
    id: "classic",
    name: "Classic",
    preview: "faces/spades-1.png",
    backs: ["default"],
    defaultBack: "default",
  },
  {
    id: "classic2",
    name: "Classic Alt",
    preview: "faces/spades-1.png",
    baseSet: "classic",
    overrides: [
      "clubs-11", "clubs-12", "clubs-13",
      "diamonds-11", "diamonds-12", "diamonds-13",
      "hearts-11", "hearts-12", "hearts-13",
      "spades-1", "spades-11", "spades-12", "spades-13",
    ],
    backs: [],
    defaultBack: "default",
  },
  {
    id: "classic3",
    name: "Classic 3 (Large)",
    preview: "faces/spades-1.png",
    backs: ["blue2", "green2", "red2"],
    defaultBack: "blue2",
    cardAspectRatio: 190 / 140, // 1.357… — Karten sind 140×190px
  },
  {
    id: "classic4",
    name: "Classic 4 (Generated)",
    preview: "faces/spades-1.png",
    backs: ["blue", "red", "green", "purple", "gray"],
    defaultBack: "blue",
    // cardAspectRatio fehlt → default 1.5 (300×450px, exakt 2:3)
  },
  {
    id: "classic5",
    name: "Classic 5 (Kein Spiegel)",
    preview: "faces/spades-1.png",
    backs: ["blue", "red", "green", "purple", "gray"],
    defaultBack: "blue",
    // Kein Spiegel, Symbol größer (270px) + nach unten versetzt (62 % der Höhe)
  },
];

// ============================================================
// Context
// ============================================================

interface CardSetContextValue {
  getFaceUrl: (card: Card) => string;
  getBackUrl: () => string;
  selectedSetId: string;
  selectedBackName: string;
  availableSets: CardSetManifest[];
  setSelectedSet: (setId: string, backName?: string) => void;
  /** Höhe/Breite der Karten im aktiven Set. Standard 1.5 (2:3). */
  cardAspectRatio: number;
  /** Sichtbarer Bereich einer verdeckten Karte (px, Desktop-Max). */
  faceDownPeek: number;
  /** Sichtbarer Bereich einer aufgedeckten Karte (px, Desktop-Max). */
  faceUpPeek: number;
  /** Aktive Tischfarbe (Key aus TABLE_BACKGROUNDS). */
  tableBackground: TableBackgroundKey;
  setTableBackground: (key: TableBackgroundKey) => void;
  /** Linkshänder-Modus: Stock/Waste rechts, Foundation links. */
  leftHanded: boolean;
  setLeftHanded: (v: boolean) => void;
  /** Fluggeschwindigkeit der Karten-Animation. */
  animationSpeed: AnimationSpeed;
  setAnimationSpeed: (v: AnimationSpeed) => void;
}

const CardSetContext = createContext<CardSetContextValue | null>(null);

export function CardSetProvider({ children }: { children: ReactNode }) {
  const [selectedSetId, setSelectedSetId] = useState("classic5");
  const [selectedBackName, setSelectedBackName] = useState("blue");
  const [tableBackground, setTableBackground] = useState<TableBackgroundKey>(
    GAME_CONFIG.tableBackground
  );
  const [leftHanded, setLeftHanded] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState<AnimationSpeed>("normal");

  const getFaceUrl = (card: Card): string => {
    const cardId = `${card.suit}-${card.rank}`;
    return getCardFaceUrl(cardId, selectedSetId, MANIFESTS);
  };

  const getBackUrl = (): string => {
    return getCardBackUrl(selectedSetId, selectedBackName, MANIFESTS);
  };

  const activeManifest = MANIFESTS.find((m) => m.id === selectedSetId);
  const cardAspectRatio = activeManifest?.cardAspectRatio ?? 1.5;
  const faceDownPeek = activeManifest?.faceDownPeek ?? GAME_CONFIG.tableauFaceDownPeek;
  const faceUpPeek   = activeManifest?.faceUpPeek   ?? GAME_CONFIG.tableauFaceUpPeek;

  const setSelectedSet = (setId: string, backName?: string) => {
    setSelectedSetId(setId);
    const manifest = MANIFESTS.find((m) => m.id === setId);
    if (backName && manifest?.backs.includes(backName)) {
      setSelectedBackName(backName);
    } else if (manifest) {
      setSelectedBackName(manifest.defaultBack);
    }
  };

  return (
    <CardSetContext.Provider
      value={{
        getFaceUrl,
        getBackUrl,
        selectedSetId,
        selectedBackName,
        availableSets: MANIFESTS,
        setSelectedSet,
        cardAspectRatio,
        faceDownPeek,
        faceUpPeek,
        tableBackground,
        setTableBackground,
        leftHanded,
        setLeftHanded,
        animationSpeed,
        setAnimationSpeed,
      }}
    >
      {children}
    </CardSetContext.Provider>
  );
}

export function useCardSet(): CardSetContextValue {
  const ctx = useContext(CardSetContext);
  if (!ctx) throw new Error("useCardSet must be used within CardSetProvider");
  return ctx;
}
